/**
 * EmailThreadConversation — the email-thread record's primary body.
 *
 * Replaces the generic overview + timeline pairing for `ahoy_email_thread`
 * (wired via the entity registry's `components.overview` slot, with the
 * generic TimelineSection suppressed through `hideTimelineSection`): instead
 * of a reverse-chronological event log, the thread reads as a conversation —
 * oldest message first, the latest expanded, earlier ones collapsed to
 * one-line summaries, grouped under absolute date headers as self-bordered
 * floating cards. Thread-level Reply renders in a sticky footer pinned to the
 * bottom of the slideout via `EmailThreadReplyFooter`; the slideout header's
 * action bar no longer shows Reply / Reply all for `ahoy_email_thread`.
 *
 * Sender/recipient identity is humanized: the view-state lookups resolve
 * addresses to CRM entity names (matching badges elsewhere in the product),
 * the envelope's display name is used next, then a name-shaped local part
 * (`jennifer.walsh@…` → "Jennifer Walsh"), and only then the raw address.
 *
 * Data source is the thread's own timeline events, fetched with the SAME
 * hook + filters as `useThreadParticipants` (the header's avatar stack) in
 * this slideout, so react-query serves both from one request. Message
 * extraction (dedup of sent/received copies, ordering, labeling) lives in
 * `../timeline/threadMessages.ts`.
 */

import { Fragment, useCallback, useMemo, useState } from 'react';
// The fold row is a full-width flat strip — a shape the styled lib/base
// Button cannot express. react-aria's Button keeps keyboard/focus/press
// semantics (EmailThreadVisibilityBadge precedent). The Expand all/Collapse
// all control uses the same primitive for consistency.
import { Button as AriaButton } from 'react-aria-components';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import type { CrmObject } from '@/types/crm';

import { ErrorBoundary, QueryErrorState } from '../../../components';
import { RECORD_BODY_BLEED } from '../record-layout';
import {
  dateGroupLabel,
  normalizeSubjectKey,
  recipientsSummaryForPayload,
  senderLabelForPayload,
  type ThreadMessage,
  viewerAvatarInitials,
} from '../timeline/threadMessages';
import { useThreadTimeline } from '../timeline/useThreadTimeline';
import {
  AVATAR_XS_GEOMETRY,
  CARD_GEOMETRY,
  CARD_STACK_GEOMETRY,
  DATE_GROUP_HEADER_GEOMETRY,
  DATE_GROUP_STACK_GEOMETRY,
  EXPANDED_BODY_PADDING,
  MESSAGE_HEADER_GEOMETRY,
} from './email-thread-geometry';
import { EmailThreadMessageCard } from './email-thread-message-card';
import { EmailThreadMessageCount } from './email-thread-message-count';

// Long threads open the way a mail client does: the first message (the
// thread's anchor) and the most recent tail stay visible; everything between
// folds behind a "Show N earlier messages" row until asked for. Bundling
// only kicks in past the threshold so short threads never hide anything.
const BUNDLE_THRESHOLD = 8;
const BUNDLE_TAIL_VISIBLE = 5;

interface EmailThreadConversationProps {
  object: CrmObject;
}

interface ConversationRow {
  message: ThreadMessage;
  senderLabel: string;
  avatarInitials: string | undefined;
  recipientsLine: string | null;
  /**
   * The message's subject, present only when it materially differs from the
   * previous message's (ignoring Re:/Fwd: prefixes) — a mid-thread subject
   * change is information the reader would otherwise lose, since the
   * conversation suppresses per-message subjects.
   */
  subjectLine: string | null;
}

interface DateGroup {
  label: string;
  rows: ConversationRow[];
  /** Index of this group's first row within visibleRows. */
  startIndex: number;
}

export function EmailThreadConversation({
  object,
}: EmailThreadConversationProps) {
  const { user } = useAuthInfo();

  // One shared thread-timeline hook across every thread surface — query,
  // paging, and message derivation live in useThreadTimeline.
  const { messages, lookups, isLoading, isFetchingMore, error, refetch } =
    useThreadTimeline({
      entityId: object.entity_id,
      orgId: object.org_id,
    });

  // The view-state lookups already map participant emails to hydrated CRM
  // entities — resolve names through them so the conversation shows the same
  // canonical identity as entity badges elsewhere, at zero extra fetch cost.
  const resolveEmailName = useCallback(
    (email: string): string | undefined => {
      const entityId = lookups?.emails?.[email];
      if (!entityId) {
        return undefined;
      }
      return lookups?.entities[entityId]?.ahoy_display_name ?? undefined;
    },
    [lookups]
  );

  // "You" is the display label for the viewer's own messages, but the avatar
  // should still carry their identity ("JD"), not a literal "Y".
  const youInitials = useMemo(
    () => viewerAvatarInitials(user?.display, user?.email),
    [user?.display, user?.email]
  );

  const rows = useMemo<ConversationRow[]>(() => {
    // Gmail semantics: the conversation's subject is the opening message's;
    // any later message whose subject materially differs (ignoring Re:/Fwd:)
    // surfaces its own — so a drift stays visible on whichever message is
    // expanded, not only on the one where it first happened.
    const anchorSubjectKey = normalizeSubjectKey(messages[0]?.payload.subject);
    return messages.map((message, index) => {
      const senderLabel =
        senderLabelForPayload(message.payload, user?.email, resolveEmailName) ??
        (message.isOutbound ? 'You' : 'Sender');
      const subjectKey = normalizeSubjectKey(message.payload.subject);
      const subjectLine =
        index > 0 && subjectKey && subjectKey !== anchorSubjectKey
          ? (message.payload.subject ?? null)
          : null;
      return {
        message,
        senderLabel,
        avatarInitials: senderLabel === 'You' ? youInitials : undefined,
        recipientsLine: recipientsSummaryForPayload(
          message.payload,
          user?.email,
          resolveEmailName
        ),
        subjectLine,
      };
    });
  }, [messages, user?.email, resolveEmailName, youInitials]);

  const latestEventId =
    rows.length > 0 ? rows[rows.length - 1].message.eventId : undefined;

  // Expansion: the latest message is open by default; every user click is an
  // explicit override keyed by event id, so new messages arriving over the
  // websocket don't clobber what the user opened or closed.
  const [expandedOverrides, setExpandedOverrides] = useState<
    Record<string, boolean>
  >({});

  // Long-thread bundling — sticky once revealed.
  const [showAllMessages, setShowAllMessages] = useState(false);

  // Tri-state so websocket arrivals inherit the user's expand intent:
  //   null  = default (only latest expanded)
  //   true  = Expand all (every row expanded, including new arrivals)
  //   false = Collapse all (every row collapsed, including new arrivals)
  const [expandAllState, setExpandAllState] = useState<null | boolean>(null);

  const handleToggle = useCallback((eventId: string, wasExpanded: boolean) => {
    setExpandedOverrides(prev => ({
      ...prev,
      [eventId]: !wasExpanded,
    }));
  }, []);

  const handleShowAllMessages = useCallback(() => {
    setShowAllMessages(true);
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandAllState(true);
    setShowAllMessages(true);
    setExpandedOverrides({});
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandAllState(false);
    setExpandedOverrides({});
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Long-thread bundling: past the threshold, keep the anchor (first message)
  // and the recent tail; the middle folds behind an explicit count row.
  const bundling = !showAllMessages && rows.length > BUNDLE_THRESHOLD;

  const visibleRows = useMemo<ConversationRow[]>(() => {
    if (!showAllMessages && rows.length > BUNDLE_THRESHOLD) {
      return [rows[0], ...rows.slice(rows.length - BUNDLE_TAIL_VISIBLE)];
    }
    return rows;
  }, [rows, showAllMessages]);

  const hiddenCount = rows.length - visibleRows.length;

  // Group visibleRows by calendar day so the fold (when bundling) naturally
  // appears between the anchor's group and the tail's group.
  const dateGroups = useMemo<DateGroup[]>(() => {
    const groups: DateGroup[] = [];
    visibleRows.forEach((row, idx) => {
      const label = dateGroupLabel(row.message.occurredAt);
      const last = groups[groups.length - 1];
      if (!last || last.label !== label) {
        groups.push({ label, rows: [row], startIndex: idx });
      } else {
        last.rows.push(row);
      }
    });
    return groups;
  }, [visibleRows]);

  let content;
  if (isLoading) {
    content = <ConversationSkeleton />;
  } else if (error) {
    content = (
      <QueryErrorState
        error={error}
        label="We couldn't load the conversation"
        onRetry={handleRetry}
        size='sm'
      />
    );
  } else if (rows.length === 0 && isFetchingMore) {
    // Pages fetched so far held no email events but more remain — still
    // walking the stream.
    content = <ConversationSkeleton />;
  } else if (rows.length === 0) {
    content = (
      <p className='text-xs md:text-sm text-[var(--color-text-tertiary)] py-2'>
        No messages yet
      </p>
    );
  } else {
    content = (
      <ErrorBoundary errorMessage="We couldn't render this conversation">
        {/* Meta row: email count (left) + Expand all/Collapse all (right). */}
        <div className='flex items-center justify-between mb-3'>
          <EmailThreadMessageCount object={object} />
          <AriaButton
            onPress={
              expandAllState === true ? handleCollapseAll : handleExpandAll
            }
            className='text-xs font-medium text-fg-brand-primary hover:underline'
            aria-label={expandAllState === true ? 'Collapse all' : 'Expand all'}
          >
            {expandAllState === true ? 'Collapse all' : 'Expand all'}
          </AriaButton>
        </div>
        {/* Date groups, each with a header strip + card stack. The fold row
            appears between the anchor group and the tail group when bundling
            (or inside the group if anchor and tail share the same calendar
            day). */}
        <div className={DATE_GROUP_STACK_GEOMETRY}>
          {dateGroups.map(group => {
            // When bundling, the first tail row starts at visibleRows[1].
            // If that row opens a new group (different day), render the fold
            // between the anchor's group and this group.
            const foldBeforeThisGroup = bundling && group.startIndex === 1;
            return (
              <Fragment key={group.label}>
                {foldBeforeThisGroup && (
                  <HiddenMessagesRow
                    count={hiddenCount}
                    onShow={handleShowAllMessages}
                  />
                )}
                <div>
                  <div className={DATE_GROUP_HEADER_GEOMETRY}>
                    <span className='text-[11px] font-medium text-[var(--color-text-tertiary)]'>
                      {group.label}
                    </span>
                    <div className='flex-1 border-t border-[var(--color-border-secondary)]' />
                  </div>
                  <div className={CARD_STACK_GEOMETRY}>
                    {group.rows.map((row, rowInGroupIdx) => {
                      const globalIdx = group.startIndex + rowInGroupIdx;
                      // Edge case: anchor and first tail share the same
                      // calendar day — fold appears within the group.
                      const foldBeforeThisCard =
                        bundling && globalIdx === 1 && !foldBeforeThisGroup;
                      const isExpanded =
                        expandedOverrides[row.message.eventId] ??
                        expandAllState ??
                        row.message.eventId === latestEventId;
                      return (
                        <Fragment key={row.message.eventId}>
                          {foldBeforeThisCard && (
                            <HiddenMessagesRow
                              count={hiddenCount}
                              onShow={handleShowAllMessages}
                            />
                          )}
                          <EmailThreadMessageCard
                            orgId={object.org_id}
                            message={row.message}
                            senderLabel={row.senderLabel}
                            avatarInitials={row.avatarInitials}
                            recipientsLine={row.recipientsLine}
                            subjectLine={row.subjectLine}
                            isExpanded={isExpanded}
                            onToggle={handleToggle}
                            // Reply on the message itself, for expanded
                            // inbound messages that are NOT the thread's
                            // latest — the latest (any direction) is the
                            // target of the header action bar's Reply.
                            showReply={
                              !row.message.isOutbound &&
                              row.message.eventId !== latestEventId
                            }
                          />
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </ErrorBoundary>
    );
  }

  return (
    // No section-header row at all: the thread's meta — including the message
    // count — lives in the record header's meta row (EmailThreadSubHeader),
    // and the thread-level Reply in the header's action bar. The count is
    // also surfaced in the conversation's own meta row above the groups.
    //
    // A reading surface wants the panel's width — bleed into the record
    // body's gutter (see record-layout.ts for the coupled pair). -mt-1
    // tightens the gap under the header divider.
    <div className={`${RECORD_BODY_BLEED} -mt-1`}>{content}</div>
  );
}

interface HiddenMessagesRowProps {
  count: number;
  onShow: () => void;
}

/**
 * The fold in a bundled long thread — a full-width row between the anchor
 * message and the visible tail. One click reveals the whole middle; the
 * conversation never re-folds on its own (see `showAllMessages`).
 */
function HiddenMessagesRow({ count, onShow }: HiddenMessagesRowProps) {
  return (
    <AriaButton
      onPress={onShow}
      className='w-full py-2 text-xs font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] transition-colors focus-visible:ring-inset'
      aria-label={`Show ${count} earlier ${count === 1 ? 'message' : 'messages'}`}
    >
      Show {count} earlier {count === 1 ? 'message' : 'messages'}
    </AriaButton>
  );
}

const PULSE = 'rounded bg-bg-tertiary animate-pulse';

// name-bar width / snippet-bar width per collapsed skeleton row — varied so
// the placeholder reads as different messages, like the real rows will.
const COLLAPSED_SKELETON_ROWS = [
  { name: 'w-28', snippet: 'max-w-[70%]' },
  { name: 'w-20', snippet: 'max-w-[55%]' },
] as const;

const BODY_SKELETON_LINES = [
  { id: 'body-1', width: 'w-full' },
  { id: 'body-2', width: 'w-11/12' },
  { id: 'body-3', width: 'w-full' },
  { id: 'body-4', width: 'w-2/3' },
] as const;

/**
 * Mirrors the loaded conversation's anatomy element-for-element so the load
 * transition doesn't jump: same meta row, a date-group header strip, collapsed
 * card placeholders, then the EXPANDED latest message card. The geometry
 * classes are the SAME constants the real components render with
 * (email-thread-geometry.ts), so the skeleton cannot drift from the layout it
 * imitates. Every pulse bar sits inside a fixed-height wrapper equal to the
 * real element's line box (text-sm → h-5, text-xs → h-4).
 */
function ConversationSkeleton() {
  return (
    <div>
      {/* Meta row placeholder */}
      <div className='flex items-center justify-between mb-3'>
        <div className='flex h-4 items-center'>
          <div className={`h-3 w-16 ${PULSE}`} />
        </div>
        <div className='flex h-4 items-center'>
          <div className={`h-3 w-14 ${PULSE}`} />
        </div>
      </div>
      {/* Date group placeholder */}
      <div className={DATE_GROUP_STACK_GEOMETRY}>
        <div>
          <div className={DATE_GROUP_HEADER_GEOMETRY}>
            <div className='flex h-4 items-center'>
              <div className={`h-2.5 w-14 ${PULSE}`} />
            </div>
            <div className='flex-1 border-t border-[var(--color-border-secondary)]' />
          </div>
          <div className={CARD_STACK_GEOMETRY}>
            {/* Collapsed card placeholders */}
            {COLLAPSED_SKELETON_ROWS.map(row => (
              <div key={row.name} className={CARD_GEOMETRY}>
                <div className={MESSAGE_HEADER_GEOMETRY}>
                  <div
                    className={`${AVATAR_XS_GEOMETRY} bg-bg-tertiary animate-pulse shrink-0`}
                  />
                  <div className='min-w-0 flex-1'>
                    <div className='flex h-5 items-center'>
                      <div className={`h-3.5 ${row.name} ${PULSE}`} />
                    </div>
                    <div className={`flex h-4 items-center ${row.snippet}`}>
                      <div className={`h-3 w-full ${PULSE}`} />
                    </div>
                  </div>
                  <div className='flex h-4 shrink-0 items-center'>
                    <div className={`h-3 w-9 ${PULSE}`} />
                  </div>
                </div>
              </div>
            ))}
            {/* Expanded latest message card placeholder */}
            <div className={CARD_GEOMETRY}>
              <div className={MESSAGE_HEADER_GEOMETRY}>
                <div
                  className={`${AVATAR_XS_GEOMETRY} bg-bg-tertiary animate-pulse shrink-0`}
                />
                <div className='min-w-0 flex-1'>
                  <div className='flex h-5 items-center'>
                    <div className={`h-3.5 w-32 ${PULSE}`} />
                  </div>
                  <div className='flex h-4 items-center'>
                    <div className={`h-3 w-44 ${PULSE}`} />
                  </div>
                </div>
                <div className='shrink-0 self-start pt-0.5'>
                  <div className='flex h-4 items-center'>
                    <div className={`h-3 w-20 ${PULSE}`} />
                  </div>
                </div>
              </div>
              <div
                className={`${EXPANDED_BODY_PADDING} border-t border-[var(--color-border-secondary)]`}
              >
                <div className='flex flex-col gap-2'>
                  {BODY_SKELETON_LINES.map(line => (
                    <div key={line.id} className='flex h-5 items-center'>
                      <div className={`h-3.5 ${line.width} ${PULSE}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
